import { Appointment, Patient } from '@medplum/fhirtypes';
import { deriveActiveLead, LeadStatus } from '@bariendo/lib/active-lead';
import { Track } from '@bariendo/lib/constants';
import { callRowStatus, getConsultAppointments } from '@bariendo/lib/intake-panel';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { PROCEDURE_OPTIONS } from '@bariendo/lib/patient-access-script';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { InsuranceCardRows } from '../Insurance/InsuranceCardRows';
import { InsuranceCard } from '../Insurance/useInsuranceCard';
import { EmployerRow } from './EmployerRow';
import { FinalLeadStatusRow, LeadStatusRow } from './LeadStatusRow';
import { PanelRow } from './PanelRow';
import { PanelSelectRow } from './PanelSelectRow';
import { TrackRow } from './TrackRow';
import { bookedOffering, serviceNote } from './services/bookedOffering';
import { dateRows } from './services/dates';
import { PanelLocks } from './services/locks';
import { consultOutcome } from './services/rows';
import { IntakeBillingData } from './useIntakeBilling';

export const LOCKED_ONCE_WON = 'Fixed once the patient is won. An admin can correct it.';
export const LOCKED_ONCE_PROCEDURE = 'Fixed once the procedure has happened. An admin can correct it.';
export const SET_BY_ORDER = 'Set by the payment request.';

const PANEL_INSURANCE_LABELS = { insurer: 'Insurance', memberId: 'Member ID', group: 'Group ID' };

interface IntakeGroupProps {
  patient: Patient;
  appointments: Appointment[];
  /** Procedure appointments only — the same slice the header pill reads. */
  procedures: Appointment[];
  data: IntakeBillingData;
  locks: PanelLocks;
  /** The order's package. Once there is one it is the Package; before, the interested service is. */
  orderPackage?: string;
  /** False for a role outside the payments roles, which reads Package without editing it. */
  canEditPackage: boolean;
  /** When the payment request was created; absent for a viewer outside the payments roles. */
  requestedAt?: string;
  wonAt?: string;
  cancellation?: { at: string; reason: string };
  insurance: InsuranceCard;
  onPackageChange: (value: string) => Promise<void>;
  onLocationChange: (value: string) => Promise<void>;
  /** Absent for a role the employer directory refuses: the row reads, with no edit. */
  onEmployerChange?: (value: string) => Promise<void>;
  onLeadStatusChange: (status: LeadStatus) => void;
  /** The patient's track, or undefined while loading / unresolved. */
  track?: Track;
  /** Absent for a viewer outside PAYMENTS_ROLES: Track reads with no Change link. */
  onChangeTrack?: () => void;
  /** Injected so the past/future boundary is testable. */
  now?: Date;
}

/**
 * "Where is this patient": quick information led by Lead status, then the dates, date first
 * (`patient-profile.mdx`). A row with nothing to say is absent, not blank.
 */
export function IntakeGroup({
  patient,
  appointments,
  procedures,
  data,
  locks,
  orderPackage,
  canEditPackage,
  requestedAt,
  wonAt,
  cancellation,
  insurance,
  onPackageChange,
  onLocationChange,
  onEmployerChange,
  onLeadStatusChange,
  track,
  onChangeTrack,
  now = new Date(),
}: IntakeGroupProps) {
  // The order confirms what was stated: once one names a package, that is the Package, and a change
  // is a new payment request rather than an edit here, which the row would never show.
  const pkg = orderPackage ?? readInterestedService(patient, data.introCallAnswers, data.eligibilityProcedure);
  const packageLock = locks.won ? LOCKED_ONCE_WON : orderPackage ? SET_BY_ORDER : undefined;
  const booked = bookedOffering(procedures);
  const { markets } = useMarketOptions();
  const market = readMarketLocation(patient, data.introCallAnswers, data.eligibilityMarket);
  // Only annotate the form's own inference. Once a rep has confirmed or edited the
  // market, the distance describes something they did not choose.
  const marketFromForm = market !== undefined && market === data.eligibilityMarket;

  const consults = getConsultAppointments(appointments);
  const consult = callRowStatus(consults, now);
  const dates = dateRows({
    signupDate: data.signupDate,
    consult,
    consultOutcome: consultOutcome(consults, consult, {
      pnOutcome: data.pnConsultOutcome,
      nextStepAnswers: data.consultOutcomes,
    }),
    requestedAt,
    coverageActions: data.coverageActions,
    wonAt,
    cancellation,
  });

  return (
    <>
      {locks.won ? (
        <FinalLeadStatusRow status="won" />
      ) : locks.cancelled ? (
        <FinalLeadStatusRow status="cancelled" />
      ) : (
        // Derived at read time from the shared rule, never stored. The menu renders off `status`,
        // not off the Flag: an override auto-clears the moment any live signal post-dates it.
        <LeadStatusRow
          state={deriveActiveLead({
            now,
            lastCommunicationSent: data.lastCommunicationSent,
            appointments,
            override: data.override,
            signupAt: data.signupDate,
          })}
          overrideAuthorName={data.overrideAuthorName}
          onChange={onLeadStatusChange}
        />
      )}
      <TrackRow track={track} onChange={onChangeTrack} lockedReason={locks.won ? LOCKED_ONCE_WON : undefined} />
      <EmployerRow value={readEmployer(patient)} onChange={onEmployerChange} />
      <InsuranceRows insurance={insurance} />
      {canEditPackage || packageLock ? (
        <PanelSelectRow
          label="Package"
          value={pkg}
          options={PROCEDURE_OPTIONS}
          onChange={onPackageChange}
          note={serviceNote(pkg, booked)}
          lockedReason={packageLock}
        />
      ) : (
        // A role that can't see the order reads Package, as Employer and Track read for roles that
        // can't change them: no chevron and no lock, which would claim the patient is won.
        <PanelRow label="Package" value={pkg ?? 'Not set'} muted={!pkg} />
      )}
      <PanelSelectRow
        label="Location"
        value={market}
        options={markets}
        onChange={onLocationChange}
        note={marketFromForm && data.eligibilityDistanceMi != null ? `· ${data.eligibilityDistanceMi} mi` : undefined}
        lockedReason={locks.procedureHappened ? LOCKED_ONCE_PROCEDURE : undefined}
      />
      {dates.map((row) => (
        <PanelRow key={row.label} label={row.label} value={row.value} />
      ))}
    </>
  );
}

/**
 * Insurance on every track, `Not set` when the patient has no insurance Coverage. Absent while the
 * card loads, for a role that can't read Coverage, and while the Insurance section's flag is off.
 */
function InsuranceRows({ insurance }: { insurance: InsuranceCard }) {
  if (!insurance.enabled) return null;
  if (insurance.state.status === 'none') return <PanelRow label="Insurance" value="Not set" muted />;
  if (insurance.state.status !== 'found') return null;

  return (
    <InsuranceCardRows
      card={insurance}
      details={insurance.state.details}
      labels={PANEL_INSURANCE_LABELS}
      hideIdsWithoutInsurer
    />
  );
}
